import { useLanguageStore } from '@/helpers/composables/useStores';
import {
	GetSingleQtvSimilarTitlesDocument,
	GetSingleQtvSimilarTitlesQuery,
	GetSingleQtvSimilarTitlesQueryVariables,
} from '@/features/buybox/graphql/queries/GetSingleQTV.query';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed } from 'vue';
import { SimilarTitlesSorting, TitleFilter } from '@/@types/graphql-types';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { useQuery } from '@/helpers/composables/useApollo';

const singleQTVBannerAndDisclaimer: Record<string, Record<string, { bannerText: string; disclaimerText: string }>> = {};

export function useSingleQTV(
	title: MaybeRefOrGetter<TitleDetail>,
	providerShortName: MaybeRefOrGetter<string>,
	enabled: MaybeRefOrGetter<boolean>,
	experimentFilters?: MaybeRefOrGetter<TitleFilter | null>
) {
	const { country, language } = useLanguageStore();
	const _title = toValue(title);
	const variables = computed<GetSingleQtvSimilarTitlesQueryVariables>(() => ({
		country: country.value,
		language: language.value,
		first: 1,
		titleId: _title.__typename === 'Season' ? _title.show.id : _title.id,
		filters: {
			packages: [toValue(providerShortName)],
			...(toValue(experimentFilters) ?? {}),
		},
		offerFilters: {
			packages: [toValue(providerShortName)],
			bestOnly: true,
		},
		sorting: SimilarTitlesSorting['ImdbScore'],
	}));

	const { result, loading, onError } = useQuery<
		GetSingleQtvSimilarTitlesQuery,
		GetSingleQtvSimilarTitlesQueryVariables
	>(GetSingleQtvSimilarTitlesDocument, variables, () => ({ enabled: toValue(enabled) }));

	onError(error => {
		captureMessageForSentry('[Graphql GetSingleQtvSimilarTitlesQuery]', {
			where: '[SingleQTV.vue]',
			error,
		});
	});

	const sqtvBannerAndDisclaimer = computed(() => {
		const provider = toValue(providerShortName);

		return singleQTVBannerAndDisclaimer[country.value]?.[provider] ?? null;
	});

	const similarTitle = computed(() =>
		result.value?.node?.__typename === 'Movie' || result.value?.node?.__typename === 'Show'
			? result?.value?.node?.similarTitlesV2.edges?.[0]
			: null
	);

	const isSQTVEmpty = computed(() => {
		if (loading.value) return false;

		return similarTitle.value == null;
	});

	return {
		similarTitle,
		loading,
		isSQTVEmpty,
		sqtvBannerAndDisclaimer,
	};
}
